/**
 * Checks if a URL has query parameters before the hash fragment.
 * This is considered malformed for hash-based routing applications.
 *
 * Example of malformed URL:
 * http://domain.com?utm_medium=email#!/courses/page
 *
 * Example of well-formed URL:
 * http://domain.com#!/courses/page?utm_medium=email
 *
 * @param url - The URL to check (defaults to window.location.href if not provided)
 * @returns true if the URL has query parameters before the hash, false otherwise
 */
/* @ngInject */
export function hasQueryParamsBeforeHash(url?: string): boolean {
  const urlToCheck = url ?? (typeof window !== 'undefined' ? window.location.href : '');

  if (!urlToCheck) {
    return false;
  }

  // Find the positions of '?' and '#!/'
  const queryIndex = urlToCheck.indexOf('?');
  const hashIndex = urlToCheck.indexOf('#!/');

  // If there's no hash-bang, it's not malformed in this context
  if (hashIndex === -1) {
    return false;
  }

  // If there's a query param and it comes before the hash-bang, it's malformed
  return queryIndex !== -1 && queryIndex < hashIndex;
}

/**
 * Checks if the current URL is malformed (has query params before hash).
 *
 * @returns true if the current URL has query parameters before the hash
 */
/* @ngInject */
export function isCurrentUrlMalformed(): boolean {
  return hasQueryParamsBeforeHash();
}

/**
 * Normalizes a URL by moving query parameters from before the hash to after it.
 *
 * Example:
 * Input:  http://domain.com?utm_medium=email#!/courses/page
 * Output: http://domain.com#!/courses/page?utm_medium=email
 *
 * @param url - The URL to normalize
 * @returns The normalized URL with query params moved after the hash
 */
/* @ngInject */
export function normalizeUrl(url: string): string {
  if (!hasQueryParamsBeforeHash(url)) {
    return url;
  }

  let urlObj: URL;
  try {
    urlObj = new URL(url);
  } catch {
    // Relative URL — retry with the current origin as base, if available.
    const base = typeof window !== 'undefined' ? window.location.origin : undefined;
    if (!base) {
      return url;
    }
    try {
      urlObj = new URL(url, base);
    } catch {
      return url;
    }
  }
  const { hash, search } = urlObj;

  // Remove query params and hash from the URL
  urlObj.search = '';
  urlObj.hash = '';

  // Reconstruct: base URL + hash + query params.
  // If the hash already contains its own query string (e.g. #!/path?foo=bar),
  // join the pre-hash params with '&' rather than '?' to avoid a double-'?'.
  const baseUrl = urlObj.toString();
  const queryToAppend = search && hash.includes('?') ? search.replace(/^\?/, '&') : search;
  const normalizedUrl = `${baseUrl}${hash}${queryToAppend}`;

  return normalizedUrl;
}

/**
 * Gets detailed information about URL formatting issues.
 *
 * @param url - The URL to analyze (defaults to window.location.href)
 * @returns An object with information about the URL structure
 */
/* @ngInject */
export function getUrlFormatInfo(url?: string): {
  isMalformed: boolean;
  hasQueryParams: boolean;
  hasHash: boolean;
  queryParamsBeforeHash: boolean;
  normalizedUrl?: string;
} {
  const urlToCheck = url ?? (typeof window !== 'undefined' ? window.location.href : '');
  const queryIndex = urlToCheck.indexOf('?');
  const hashIndex = urlToCheck.indexOf('#!/');

  const hasQueryParams = queryIndex !== -1;
  const hasHash = hashIndex !== -1;
  const queryParamsBeforeHash = hasQueryParams && hasHash && queryIndex < hashIndex;

  return {
    isMalformed: queryParamsBeforeHash,
    hasQueryParams,
    hasHash,
    queryParamsBeforeHash,
    normalizedUrl: queryParamsBeforeHash ? normalizeUrl(urlToCheck) : undefined,
  };
}
